import { Injectable, TemplateRef } from '@angular/core';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  text: string | TemplateRef<any>;
  type: ToastType;
  icon: string;
  /**
   * Classes antigas do toast (`bg-success text-light`...). Ficam só como gancho: os specs Cypress
   * de `cypress/e2e/general` procuram o toast por elas. O visual é o do layout novo, por `type`.
   */
  classname: string;
  delay: number;
  timer?: ReturnType<typeof setTimeout>;
}

const ICONS: Record<ToastType, string> = {
  success: 'fa-check-circle',
  error: 'fa-times-circle',
  info: 'fa-info-circle',
  warning: 'fa-exclamation-triangle',
};

const LEGACY_CLASSES: Record<ToastType, string> = {
  success: 'text-light bg-success',
  error: 'text-light bg-danger',
  info: 'text-light bg-info',
  warning: 'bg-warning',
};

@Injectable({
  providedIn: 'root'
})
export class ToastService {

  toasts: Toast[] = [];

  /**
   * O mesmo aviso ainda na tela não empilha outro igual (09/10/2026, pedido do Bruno): o tempo dele
   * recomeça. Na automação, o "Ativa?" (e o antigo "Enviar para") gravava sozinho, e o Salvar logo depois
   * empilhava dois "Automação alterada com sucesso!"; o "Sincronizando informações do Active
   * Campaign..." saía a cada modal aberto.
   */
  private show(text: string | TemplateRef<any>, type: ToastType, delay: number): void {
    const same = this.toasts.find(t => t.text === text && t.type === type);
    if (same) {
      this.schedule(same, delay);
      return;
    }

    const toast: Toast = { text, type, icon: ICONS[type], classname: LEGACY_CLASSES[type], delay };
    this.toasts = [...this.toasts, toast];
    this.schedule(toast, delay);
  }

  private schedule(toast: Toast, delay: number): void {
    clearTimeout(toast.timer);
    toast.delay = delay;
    toast.timer = setTimeout(() => this.remove(toast), delay);
  }

  success(message: string, delay: number = 5000): void {
    this.show(message, 'success', delay);
  }

  error(message: string, delay: number = 5000): void {
    this.show(message, 'error', delay);
  }

  info(message: string, delay: number = 5000): void {
    this.show(message, 'info', delay);
  }

  warning(message: string, delay: number = 5000): void {
    this.show(message, 'warning', delay);
  }

  remove(toast: Toast): void {
    clearTimeout(toast.timer);
    this.toasts = this.toasts.filter(t => t !== toast);
  }
}
